import { LoadingOverlay } from '@mantine/core'
import { useRouter } from 'next/router'
import React from 'react'

import { Redirect, ViewDenied, ViewError } from '@/components/commons/'
import { useFetch } from '@/lib/hooks'
import { isEmailConfirmationRequired } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { SiteData as SiteDataType } from '@/types'

import ProtectedRequest from '../SiteGuard/components/ProtectedRequest'

interface Props {
  initialData?: SiteDataType
  children?: React.ReactNode
}

export default function AccountsGuard({ initialData, children }: Props) {
  // Hooks
  const router = useRouter()
  const { isAuthenticated, permissionsData, isEmailConfirmed } = useAuth() || {}

  // Constants
  const { site: siteSlug } = router.query || {}
  const { isAuthorized } = permissionsData?.site || {}

  // Fetch
  const { data: siteData, error } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site // rehydration if necessary
  })

  const isLoading = (!siteData && !error) || isAuthenticated === null

  // Render
  if (isLoading) return <LoadingOverlay visible /> // Loading

  if (siteData?.maintenance) return <Redirect url="/maintenance" replace />

  if (!isAuthenticated) {
    if (router.asPath === '/accounts/logout') return <Redirect url="/" />

    return <Redirect url="/accounts/login" callbackUrl={router.asPath} replace />
  }

  if (siteData?.privacy === 'protected' && !!isAuthenticated && !isAuthorized) {
    return <ProtectedRequest permissions={permissionsData} />
  }

  if (siteData?.privacy === 'private' && !!isAuthenticated && !isAuthorized) {
    return <ViewDenied />
  }

  if (error) {
    return (
      <ViewError
        title="Erro"
        message="Ops... Houve um erro ao carregar esta página. Tente novamente ou entre em contato conosco."
      />
    ) // Error view if initial data and retrieve failed
  }

  if (
    isEmailConfirmationRequired(siteData?.requireEmailConfirmation, isEmailConfirmed) &&
    !router.asPath.includes('/accounts/confirm-email')
  ) {
    return <Redirect url="/accounts/confirm-email" callbackUrl={router.asPath} replace />
  }

  return <>{children}</>
}
